import {computed, type Ref} from 'vue';
import {fuzzyMatchItems, type FuzzyMatchResult} from './use-fuzzy-string-matching';
import type {FeelingOption, RatedFeelingOption} from '@/ts/types/component/feeling-selection';

/**
 * Enhances fuzzy match results with feeling-specific logic:
 * - Related items boosting
 * - Inappropriate phrase handling
 */
function enhanceFeelingResults(
  results: FuzzyMatchResult<FeelingOption>[],
  query: string,
  inappropriatePhraseDict: Record<string, string[]>,
): RatedFeelingOption[] {
  const queryLower = query?.toLowerCase() ?? '';

  // Convert to mutable results for enhancement
  const enhancedResults = results.map(result => ({
    ...result.item,
    distance: result.distance,
  }));

  const lowDistanceResults = enhancedResults.filter(item => item.distance < 1);

  if (lowDistanceResults.length < 3) {
    // Apply related items boosting
    lowDistanceResults.forEach(hitItem => {
      const relatedItems = enhancedResults.filter(item =>
        hitItem.related.includes(item.id));
      relatedItems.forEach(item => {
        item.distance = 1.1;
      });
    });

    // Handle inappropriate phrases by suggesting alternatives
    const alternativeWords = Object.keys(inappropriatePhraseDict).reduce<string[]>(
      (acc, phrase) => {
        if (queryLower?.includes(phrase) ?? false) {
          return [...acc, ...inappropriatePhraseDict[phrase]];
        }
        return acc;
      },
      [],
    );

    [...new Set(alternativeWords)].forEach((alternativeWord) => {
      const alternativeResult = enhancedResults.find(item => 
        item.name === alternativeWord);
      if (alternativeResult) {
        alternativeResult.distance = Math.min(alternativeResult.distance, 1.2);
      }
    });
  }

  return enhancedResults
    .filter(item => item.distance < 3)
    .sort((a, b) => a.distance - b.distance)
    .slice(0, 5);
}

/**
 * Composable for fuzzy matching feeling options with feeling-specific enhancements.
 * Uses conditional fuzzy matching for performance optimization.
 */
export function useFeelingFuzzyMatching(
  options: Ref<FeelingOption[]>,
  query: Ref<string>,
  inappropriatePhraseDict: Record<string, string[]>,
): Ref<RatedFeelingOption[]> {
  return computed(() => {
    const queryInput = query.value.toLowerCase();

    if (queryInput.length < 2) {
      return [];
    }

    const baseResults = fuzzyMatchItems(
      options.value,
      query.value,
      (option) => option.name,
      {
        alwaysUseFuzzy: false, // Only use fuzzy when needed for performance
        fuzzyThreshold: 5,
        maxResults: 20, // Get more results before feeling-specific filtering
        maxDistance: 3,
      },
    );

    // Apply feeling-specific enhancements
    return enhanceFeelingResults(baseResults, query.value, inappropriatePhraseDict);
  });
}
